import { Vec2 } from './Vec2';

export interface ClosestPointResult {
  point: Vec2;
  t: number; // Normalized 0..1 along segment
  distance: number;
}

export interface SweepHitResult {
  hit: boolean;
  t: number; // Time of impact 0..1 along ball movement
  paddleT: number; // Position along paddle 0..1
  contactPoint: Vec2;
  normal: Vec2;
}

export class Geometry {
  /**
   * Finds the closest point on segment AB to point P.
   */
  static closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): ClosestPointResult {
    const ab = b.sub(a);
    const abLenSq = ab.lengthSq();

    if (abLenSq === 0) {
      return { point: a.clone(), t: 0, distance: p.distanceTo(a) };
    }

    const ap = p.sub(a);
    let t = ap.dot(ab) / abLenSq;
    t = Math.max(0, Math.min(1, t));

    const point = a.add(ab.scale(t));
    const distance = p.distanceTo(point);

    return { point, t, distance };
  }

  /**
   * Continuous Collision Detection (CCD)
   * Sweeps a moving sphere (radius r) from `start` to `end` against a line segment AB.
   * Returns hit info or null if no collision occurs during this step.
   */
  static sweepCircleSegment(
    start: Vec2,
    end: Vec2,
    radius: number,
    a: Vec2,
    b: Vec2
  ): SweepHitResult | null {
    const d = end.sub(start);
    const ab = b.sub(a);
    const abLen = ab.length();
    if (abLen === 0) return null;

    // Normal pointing inwards/perpendicular to segment AB
    const tangent = ab.scale(1 / abLen);
    const normal = new Vec2(-tangent.y, tangent.x);

    // Initial check: if already touching segment at start
    const startClosest = this.closestPointOnSegment(start, a, b);
    if (startClosest.distance <= radius) {
      const velDotNorm = d.dot(normal);
      const effectiveNormal = velDotNorm < 0 ? normal : normal.scale(-1);
      return {
        hit: true,
        t: 0,
        paddleT: startClosest.t,
        contactPoint: startClosest.point,
        normal: effectiveNormal
      };
    }

    // Line-line intersection between ball path [start, end] and offset segment
    // Test for collision with segment expanded by radius
    const steps = Math.ceil(d.length() / (radius * 0.75));
    const clampedSteps = Math.max(1, Math.min(steps, 8)); // sub-stepping for CCD

    for (let i = 1; i <= clampedSteps; i++) {
      const stepT = i / clampedSteps;
      const currentPos = start.lerp(end, stepT);
      const closest = this.closestPointOnSegment(currentPos, a, b);

      if (closest.distance <= radius) {
        // Impact detected within this sub-step
        const velDotNorm = d.dot(normal);
        // Normal should oppose incoming velocity
        const effectiveNormal = velDotNorm < 0 ? normal : normal.scale(-1);

        return {
          hit: true,
          t: stepT,
          paddleT: closest.t,
          contactPoint: closest.point,
          normal: effectiveNormal
        };
      }
    }

    return null;
  }

  /**
   * Standard 2D ray-segment intersection.
   */
  static raySegmentIntersect(
    rayOrigin: Vec2,
    rayDir: Vec2,
    a: Vec2,
    b: Vec2
  ): { hit: boolean; t: number; point: Vec2 } | null {
    const x1 = rayOrigin.x;
    const y1 = rayOrigin.y;
    const x2 = rayOrigin.x + rayDir.x;
    const y2 = rayOrigin.y + rayDir.y;

    const x3 = a.x;
    const y3 = a.y;
    const x4 = b.x;
    const y4 = b.y;

    const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(den) < 1e-6) return null;

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / den;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / den;

    if (t > 0 && u >= 0 && u <= 1) {
      return {
        hit: true,
        t,
        point: new Vec2(x1 + t * (x2 - x1), y1 + t * (y2 - y1))
      };
    }

    return null;
  }

  /**
   * Tests whether line segment [p1, p2] intersects line segment [a, b].
   * Returns hit info only if intersection occurs within both segments (0 <= t <= 1 and 0 <= u <= 1).
   */
  static segmentSegmentIntersect(
    p1: Vec2,
    p2: Vec2,
    a: Vec2,
    b: Vec2
  ): { hit: boolean; t: number; point: Vec2 } | null {
    const x1 = p1.x;
    const y1 = p1.y;
    const x2 = p2.x;
    const y2 = p2.y;

    const x3 = a.x;
    const y3 = a.y;
    const x4 = b.x;
    const y4 = b.y;

    const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(den) < 1e-6) return null;

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / den;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / den;

    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return {
        hit: true,
        t,
        point: new Vec2(x1 + t * (x2 - x1), y1 + t * (y2 - y1)),
      };
    }

    return null;
  }
}
